import { C as FARBIG, PALETTES, FONT, FONT_MEDIUM, FONT_BOLD, FONT_ITALIC } from './tokens.js';
import { vectorSource, treeSource, GLYPHS, SPRITE } from './tiles.js';


const SWISSTOPO_ATTRIB =
  '<a href="https://www.swisstopo.admin.ch" target="_blank" rel="noopener">&copy; swisstopo</a>';
// SWISSIMAGE stammt teils aus Satellitenaufnahmen und verlangt laut swisstopo
// eine eigene Quellenangabe - nicht bloss "swisstopo".
const SWISSIMAGE_ATTRIB =
  '<a href="https://www.swisstopo.admin.ch" target="_blank" rel="noopener">' +
  'CNES, Spot Image, swisstopo, NPOC</a>';
const WMTS = (layer, fmt = 'png') =>
  `https://wmts.geo.admin.ch/1.0.0/${layer}/default/current/3857/{z}/{x}/{y}.${fmt}`;

/** Zoomabhängige Linienbreite: [[z, w], ...] -> interpolate-Ausdruck */
const w = (stops) => ['interpolate', ['linear'], ['zoom'], ...stops.flat()];

/**
 * Schriftgrad waechst exponentiell mit, nicht linear - sonst wirkt die
 * Beschriftung beim Hineinzoomen schnell zu klein. Basis 1.25 wie in
 * swisstopos eigenen Vektorkacheln; die Stuetzwerte sind von dort abgelesen.
 */
const ts = (stops) => ['interpolate', ['exponential', 1.25], ['zoom'], ...stops.flat()];

/** Beschriftung bevorzugt deutsch, sonst lokaler Name */
const NAME = ['coalesce', ['get', 'name:de'], ['get', 'name_de'], ['get', 'name']];

/**
 * Wichtige Punkte früh, Nebensächliches spät. Haltestellen sind in OSM sehr
 * dicht und kämen sonst schon bei Zoom 14 flächendeckend.
 */
export const POI_SPLIT = {
  'poi-symbol': ['all', ['<=', ['get', 'rank'], 8], ['!=', ['get', 'class'], 'bus']],
  'poi-minor': ['any', ['>', ['get', 'rank'], 8], ['==', ['get', 'class'], 'bus']]
};

const fill = (id, sourceLayer, filter, color, extra = {}) => ({
  id,
  type: 'fill',
  source: 'osm',
  'source-layer': sourceLayer,
  ...(filter ? { filter } : {}),
  paint: { 'fill-color': color, 'fill-antialias': true, ...extra }
});

const line = (id, sourceLayer, filter, paint, layout = {}) => ({
  id,
  type: 'line',
  source: 'osm',
  'source-layer': sourceLayer,
  ...(filter ? { filter } : {}),
  layout: { 'line-cap': 'round', 'line-join': 'round', ...layout },
  paint
});

/**
 * Strassenklassen. In kleinen Massstäben bleiben die Linien schlank und
 * wachsen erst ab Zoom 14 auf die Breite, die im Kartenbild gewünscht ist.
 */
const roadClasses = (C) => ({
  motorway: {
    case: C.motorwayCase, fill: C.motorway, minzoom: 5,
    width: [[5, 0.6], [8, 1.2], [10, 1.8], [12, 3.2], [13, 4.2], [14, 6.5], [15, 11], [16, 20], [20, 48]]
  },
  trunk: {
    case: C.trunkCase, fill: C.trunk, minzoom: 6,
    width: [[6, 0.5], [9, 1.1], [12, 2.6], [14, 5], [16, 14], [20, 36]]
  },
  primary: {
    case: C.primaryCase, fill: C.primary, minzoom: 7,
    width: [[7, 0.5], [10, 1.2], [12, 2.4], [14, 4.6], [16, 12], [20, 32]]
  },
  secondary: {
    case: C.secondaryCase, fill: C.secondary, minzoom: 9,
    width: [[9, 0.5], [12, 1.8], [14, 3.2], [16, 9], [20, 24]]
  },
  tertiary: {
    case: C.tertiaryCase, fill: C.tertiary, minzoom: 10,
    width: [[10, 0.5], [13, 1.6], [16, 8], [20, 20]]
  },
  minor: {
    case: C.minorCase, fill: C.minor, minzoom: 12,
    width: [[12, 0.7], [14, 2.2], [16, 6], [20, 18]]
  },
  service: {
    case: C.serviceCase, fill: C.service, minzoom: 14,
    width: [[14, 0.9], [16, 3.2], [20, 12]]
  }
});

/** Der Rand wächst mit der Linie mit, statt bei kleinem Massstab zu dominieren. */
const casingWidth = (stops, factor) => stops.map(([z, v]) => [z, v * factor + 0.5]);

function roadLayers(C) {
  const ROAD_CLASSES = roadClasses(C);
  const layers = [];
  const order = ['service', 'minor', 'tertiary', 'secondary', 'primary', 'trunk', 'motorway'];

  // Erst alle Casings, dann alle Füllungen -> saubere Kreuzungen
  for (const key of order) {
    const r = ROAD_CLASSES[key];
    layers.push(
      line(
        `road-${key}-case`,
        'transportation',
        ['all', ['==', ['get', 'class'], key], ['!=', ['get', 'brunnel'], 'tunnel']],
        {
          'line-color': r.case,
          'line-width': w(casingWidth(r.width, key === 'motorway' ? 1.4 : 1.32))
        },
        { 'line-cap': 'butt' }
      )
    );
    layers[layers.length - 1].minzoom = r.minzoom;
  }
  for (const key of order) {
    const r = ROAD_CLASSES[key];
    layers.push(
      line(
        `road-${key}`,
        'transportation',
        ['all', ['==', ['get', 'class'], key], ['!=', ['get', 'brunnel'], 'tunnel']],
        { 'line-color': r.fill, 'line-width': w(r.width) }
      )
    );
    layers[layers.length - 1].minzoom = r.minzoom;
  }
  return layers;
}

export function buildStyle({ palette = 'farbig' } = {}) {
  const C = PALETTES[palette] || FARBIG;
  // Breite wie bei swisstopo zoomabhaengig: in der Uebersicht fast nichts,
  // im grossen Massstab knapp ueber einem Pixel. Das Luftbild hebt sie an.
  const hf = C.haloFactor ?? 1;
  const haloW = (max) => w([[12, 0.5 * hf], [14, 0.9 * hf], [16, max * hf]]);
  return {
    version: 8,
    name: 'KGVA Landeskarte',
    glyphs: GLYPHS,
    sprite: SPRITE,
    sources: {
      osm: vectorSource(),
      relief: {
        type: 'raster',
        tiles: [WMTS('ch.swisstopo.leichte-basiskarte_reliefschattierung')],
        tileSize: 256,
        minzoom: 2,
        maxzoom: 17,
        attribution: SWISSTOPO_ATTRIB
      },
      aerial: {
        type: 'raster',
        tiles: [WMTS('ch.swisstopo.swissimage', 'jpeg')],
        tileSize: 256,
        minzoom: 2,
        maxzoom: 19,
        attribution: SWISSIMAGE_ATTRIB
      },
      // Offizielle Vektorkacheln von swisstopo: Hoehenkurven, Hoehenkoten und
      // Flurnamen aus der Landeskarte - genauer als alles aus einem globalen
      // Hoehenmodell gerechnete, aber nur fuer die Schweiz.
      swisstopo: {
        type: 'vector',
        url: 'https://vectortiles.geo.admin.ch/tiles/ch.swisstopo.base.vt/v1.0.0/tiles.json',
        attribution: SWISSTOPO_ATTRIB
      },
      ...(treeSource() ? { trees: treeSource() } : {})
    },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': C.paper } },

      // Umschaltbare Hintergründe (nur Schweiz), standardmässig aus
      {
        id: 'bg-aerial',
        type: 'raster',
        source: 'aerial',
        layout: { visibility: 'none' },
        paint: { 'raster-opacity': 1 }
      },

      // ---------- Bodenbedeckung ----------
      // Unter Zoom 14 liefern die Kacheln kaum Gebäude – dann trägt die
      // Siedlungsfläche das Bild, wie im kleinen Massstab der Landeskarte auch.
      fill('lc-settlement', 'landuse', ['match', ['get', 'class'], ['residential', 'suburb', 'quarter', 'neighbourhood'], true, false], C.settlement, {
        'fill-opacity': ['interpolate', ['linear'], ['zoom'], 9, 0.55, 12, 1, 13.9, 1, 14.2, 0.5, 16, 0.35]
      }),
      fill('lc-farmland', 'landcover', ['==', ['get', 'class'], 'farmland'], C.farmland),
      fill('lc-grass', 'landcover', ['match', ['get', 'class'], ['grass', 'wetland'], true, false], C.grass),
      fill('lc-sand', 'landcover', ['==', ['get', 'class'], 'sand'], C.sand),
      fill('lc-rock', 'landcover', ['==', ['get', 'class'], 'rock'], C.rock),
      fill('lc-glacier', 'landcover', ['==', ['get', 'class'], 'ice'], C.glacier),
      fill('lc-scrub', 'landcover', ['match', ['get', 'subclass'], ['scrub', 'heath'], true, false], C.scrub),
      fill('lc-forest', 'landcover', ['==', ['get', 'class'], 'wood'], C.forest),
      line('lc-forest-edge', 'landcover', ['==', ['get', 'class'], 'wood'], {
        'line-color': C.forestEdge,
        'line-width': w([[10, 0.4], [14, 1]]),
        'line-opacity': 0.8
      }),
      fill('lc-park', 'park', null, C.grass, { 'fill-opacity': 0.45 }),

      // ---------- Relief (optional, nur CH) ----------
      {
        id: 'relief-shading',
        type: 'raster',
        source: 'relief',
        layout: { visibility: 'none' },
        paint: {
          'raster-opacity': 0.45,
          'raster-contrast': 0.05,
          // Die Schattierung trägt einen leichten Grünstich - im Schwarzweiss-
          // Modus muss der raus, sonst ist die Karte nicht wirklich grau.
          'raster-saturation': palette === 'sw' ? -1 : 0
        }
      },

      // ---------- Einzelbäume ----------
      // Die Landeskarte hebt einzelne Bäume hervor; im OpenMapTiles-Schema
      // fehlen sie, darum ein eigenes kleines Archiv (deploy/trees.yml).
      {
        id: 'tree-row',
        type: 'line',
        source: 'trees',
        'source-layer': 'tree_row',
        minzoom: 15,
        layout: { 'line-cap': 'round' },
        paint: {
          'line-color': C.tree,
          'line-width': w([[15, 1.4], [18, 3]]),
          'line-dasharray': [0.1, 1.6]
        }
      },
      {
        id: 'tree-single',
        type: 'circle',
        source: 'trees',
        'source-layer': 'tree',
        minzoom: 15,
        paint: {
          'circle-color': ['match', ['get', 'leaf_type'], 'needleleaved', C.treeConifer, C.tree],
          'circle-radius': w([[15, 1], [17, 2.1], [19, 3.4]]),
          'circle-opacity': ['interpolate', ['linear'], ['zoom'], 15, 0.55, 16.5, 0.9]
        }
      },

      // ---------- Höhenlinien ----------
      // Zwischenkurven alle 10 m, Hauptkurven alle 100 m - so zeichnet es die
      // Landeskarte. Die Werte kommen direkt aus swisstopos Kurvensatz.
      {
        id: 'contour-minor',
        type: 'line',
        source: 'swisstopo',
        'source-layer': 'contour_line',
        minzoom: 12,
        filter: ['all', ['==', ['get', 'class'], 'land'], ['!=', ['%', ['get', 'ele'], 100], 0]],
        layout: { 'line-join': 'round' },
        paint: {
          'line-color': C.contour,
          'line-width': w([[12, 0.4], [16, 0.7], [18, 0.9]]),
          'line-opacity': ['interpolate', ['linear'], ['zoom'], 12, 0.3, 14, 0.75]
        }
      },
      {
        id: 'contour-main',
        type: 'line',
        source: 'swisstopo',
        'source-layer': 'contour_line',
        minzoom: 10,
        filter: ['all', ['==', ['get', 'class'], 'land'], ['==', ['%', ['get', 'ele'], 100], 0]],
        layout: { 'line-join': 'round' },
        paint: {
          'line-color': C.contourMain,
          'line-width': w([[10, 0.7], [14, 1.1], [18, 1.6]]),
          'line-opacity': ['interpolate', ['linear'], ['zoom'], 10, 0.4, 13, 0.8]
        }
      },

      {
        id: 'contour-label',
        type: 'symbol',
        source: 'swisstopo',
        'source-layer': 'contour_line',
        minzoom: 12.5,
        filter: ['all', ['==', ['get', 'class'], 'land'], ['==', ['%', ['get', 'ele'], 100], 0]],
        layout: {
          'symbol-placement': 'line',
          'text-field': ['to-string', ['get', 'ele']],
          'text-font': FONT_ITALIC,
          'text-size': ts([[13, 10], [14, 10.5], [16, 12], [18, 13]]),
          'text-max-angle': 25,
          'text-padding': 8,
          'text-rotation-alignment': 'map'
        },
        paint: { 'text-color': C.contourLabel, 'text-halo-color': C.haloContour, 'text-halo-width': haloW(1), 'text-halo-blur': 0.25 }
      },

      // ---------- Wasser ----------
      fill('water-fill', 'water', ['!=', ['get', 'brunnel'], 'tunnel'], C.water),
      line('water-edge', 'water', ['!=', ['get', 'brunnel'], 'tunnel'], {
        'line-color': C.waterEdge,
        'line-width': w([[6, 0.3], [10, 0.7], [14, 1.3]]),
        'line-opacity': 0.9
      }),
      line('water-river', 'waterway', ['match', ['get', 'class'], ['river', 'canal'], true, false], {
        'line-color': C.river,
        'line-width': w([[8, 0.6], [12, 1.8], [16, 4.5], [20, 12]])
      }),
      line('water-stream', 'waterway', ['match', ['get', 'class'], ['stream', 'ditch', 'drain'], true, false], {
        'line-color': C.river,
        'line-width': w([[12, 0.5], [16, 1.6], [20, 5]]),
        'line-opacity': 0.85
      }),

      // Schiffsrouten
      line('water-ferry', 'transportation', ['==', ['get', 'class'], 'ferry'], {
        'line-color': C.waterEdge,
        'line-width': w([[9, 0.6], [14, 1.2], [18, 2]]),
        'line-dasharray': [4, 3],
        'line-opacity': 0.8
      }),

      // ---------- Tunnel (gedämpft, unter den Strassen) ----------
      line('road-tunnel', 'transportation', ['all', ['==', ['get', 'brunnel'], 'tunnel'], ['match', ['get', 'class'], ['motorway', 'trunk', 'primary', 'secondary', 'tertiary', 'minor'], true, false]], {
        'line-color': C.minorCase,
        'line-width': w([[12, 1.5], [16, 6], [20, 16]]),
        'line-dasharray': [2, 2],
        'line-opacity': 0.55
      }),

      // ---------- Wege ----------
      line('road-track', 'transportation', ['==', ['get', 'class'], 'track'], {
        'line-color': C.track,
        'line-width': w([[13, 0.6], [16, 1.3], [20, 3]]),
        'line-dasharray': [5, 2.5]
      }),
      // Trottoirs, Treppen, Velowege: braun gestrichelt
      line('road-path', 'transportation', ['all', ['==', ['get', 'class'], 'path'], ['!=', ['get', 'subclass'], 'path']], {
        'line-color': C.path,
        'line-width': w([[14, 0.7], [16, 1.4], [20, 4]]),
        'line-dasharray': [2, 2]
      }),
      // Wanderwege: auf der Landeskarte rot gestrichelt
      line('path-hiking', 'transportation', ['all', ['==', ['get', 'class'], 'path'], ['==', ['get', 'subclass'], 'path']], {
        'line-color': C.hiking,
        'line-width': w([[12, 0.8], [15, 1.3], [18, 2.2], [20, 3.4]]),
        'line-dasharray': [3, 2]
      }),

      // ---------- Flugplätze ----------
      fill('aeroway-area', 'aeroway', ['match', ['get', 'class'], ['runway', 'taxiway', 'apron'], true, false], C.aeroway),

      ...roadLayers(C),

      // ---------- Bahn ----------
      line('rail-base', 'transportation', ['all', ['==', ['get', 'class'], 'rail'], ['!=', ['get', 'brunnel'], 'tunnel']], {
        'line-color': C.rail,
        'line-width': w([[8, 0.4], [10, 0.7], [12, 1.2], [14, 2.2], [16, 3.6], [20, 7]])
      }),
      // Bahntunnel: rot gestrichelt wie im Kartenbild
      line('rail-tunnel', 'transportation', ['all', ['==', ['get', 'class'], 'rail'], ['==', ['get', 'brunnel'], 'tunnel']], {
        'line-color': C.rail,
        'line-width': w([[10, 0.6], [12, 1], [14, 1.8], [16, 2.8], [20, 5]]),
        'line-dasharray': [3, 2.5],
        'line-opacity': 0.85
      }),
      // Tram, Metro, Standseilbahn: schlanker und gestrichelt
      line('rail-light', 'transportation', ['match', ['get', 'class'], ['transit'], true, false], {
        'line-color': C.railLight,
        'line-width': w([[12, 0.5], [14, 1.1], [16, 1.8], [20, 3.4]]),
        'line-dasharray': [4, 2]
      }),

      // ---------- Gebäude ----------
      // Zoom 13 ist die unterste Stufe mit vollstaendigem Bestand. Die Kachel
      // meldet dort zwar nur *ein* Objekt - aber Planetiler verschmilzt auf
      // dieser Stufe alle benachbarten Gebaeude zu einem Mehrfachpolygon mit
      // tausenden Ringen, das Bild ist also komplett. Bei Zoom 12 ist die
      // Ebene wirklich leer, tiefer geht es nicht.
      //
      // Alle gemeinsam ein- und ausblenden, nicht stufenweise: sonst wirken
      // Einfamilienhausquartiere leer, obwohl sie dicht bebaut sind.
      { ...fill('building-fill', 'building', null, C.building, { 'fill-opacity': 1 }), minzoom: 13 },
      // Nur im hellen Bild: ohne Kante verschwimmen graue Haeuser zu einer Flaeche.
      ...(C.buildingEdge
        ? [{
            ...line('building-edge', 'building', null, {
              'line-color': C.buildingEdge,
              'line-width': w([[14, 0.4], [17, 0.9]])
            }),
            minzoom: 14
          }]
        : []),

      // ---------- Grenzen ----------
      line('boundary-canton', 'boundary', ['all', ['==', ['get', 'admin_level'], 4], ['!=', ['get', 'maritime'], 1]], {
        'line-color': C.borderCanton,
        'line-width': w([[6, 0.6], [10, 1.4], [14, 2.4]]),
        'line-dasharray': [6, 2, 1, 2],
        'line-opacity': 0.8
      }),
      // Erst der Saum, dann der Strich darueber - so liest sich die Grenze auch
      // dort noch, wo sie durch dichte Bebauung laeuft.
      line('boundary-country-band', 'boundary', ['all', ['<=', ['get', 'admin_level'], 2], ['!=', ['get', 'maritime'], 1]], {
        'line-color': C.borderBand,
        'line-width': w([[4, 2.5], [8, 6], [12, 11], [16, 20]]),
        'line-opacity': 0.55,
        'line-blur': 1
      }),
      line('boundary-country', 'boundary', ['all', ['<=', ['get', 'admin_level'], 2], ['!=', ['get', 'maritime'], 1]], {
        'line-color': C.borderCountry,
        'line-width': w([[3, 0.6], [8, 1.1], [14, 1.8]]),
        'line-opacity': 0.9
      }),

      // ---------- Beschriftung ----------
      {
        id: 'label-water',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'water_name',
        minzoom: 7,
        layout: {
          'text-field': NAME,
          'text-font': FONT_ITALIC,
          'text-size': ts([[8, 11], [14, 15], [18, 20]]),
          'text-max-width': 7,
          'symbol-placement': 'point'
        },
        paint: { 'text-color': C.waterLabel, 'text-halo-color': C.haloWater, 'text-halo-width': haloW(1), 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-waterway',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'waterway',
        minzoom: 12,
        filter: ['match', ['get', 'class'], ['river', 'canal'], true, false],
        layout: {
          'text-field': NAME,
          'text-font': FONT_ITALIC,
          'text-size': ts([[12, 10], [14, 11.5], [18, 15]]),
          'symbol-placement': 'line',
          'text-letter-spacing': 0.1
        },
        paint: { 'text-color': C.waterLabel, 'text-halo-color': C.haloWater, 'text-halo-width': haloW(1), 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-stream',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'waterway',
        minzoom: 14,
        filter: ['==', ['get', 'class'], 'stream'],
        layout: {
          'text-field': NAME,
          'text-font': FONT_ITALIC,
          'text-size': ts([[13, 9.5], [16, 11.5], [18, 13]]),
          'symbol-placement': 'line',
          'text-letter-spacing': 0.08,
          'symbol-spacing': 400
        },
        paint: { 'text-color': C.waterLabel, 'text-halo-color': C.haloWater, 'text-halo-width': haloW(1), 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-road',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'transportation_name',
        minzoom: 13,
        filter: ['match', ['get', 'class'], ['ferry', 'path', 'track'], false, true],
        layout: {
          'text-field': NAME,
          'text-font': FONT,
          'text-size': ts([[13, 10], [16, 12], [18, 14]]),
          'symbol-placement': 'line',
          'text-rotation-alignment': 'map',
          'text-padding': 4
        },
        paint: { 'text-color': C.inkSoft, 'text-halo-color': C.halo, 'text-halo-width': haloW(1), 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-peak',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'mountain_peak',
        minzoom: 10,
        layout: {
          'text-field': ['format', NAME, {}, '\n', {}, ['concat', ['to-string', ['get', 'ele']], ' m'], { 'font-scale': 0.8 }],
          'text-font': FONT,
          'text-size': ts([[10, 10], [13, 12], [18, 15]]),
          'text-anchor': 'left',
          'text-offset': [0.7, 0],
          'icon-image': 'triangle_11',
          'icon-size': 0.9,
          'text-optional': true
        },
        paint: {
          'text-color': C.peak,
          'text-halo-color': C.halo,
          'text-halo-width': haloW(1), 'text-halo-blur': 0.25,
          'icon-color': C.peak,
          'icon-halo-color': C.halo,
          'icon-halo-width': haloW(1)
        }
      },
      {
        id: 'poi-minor',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'poi',
        minzoom: 16,
        filter: ['all', ['==', ['geometry-type'], 'Point'], POI_SPLIT['poi-minor']],
        layout: {
          'icon-image': ['coalesce', ['image', ['get', 'class']], ['image', 'dot_11']],
          'icon-size': 0.85,
          'text-field': ['step', ['zoom'], '', 17, NAME],
          'text-font': FONT,
          'text-size': ts([[13, 10.5], [18, 14]]),
          'text-anchor': 'top',
          'text-offset': [0, 0.75],
          'text-max-width': 8,
          'text-optional': true
        },
        paint: {
          'text-color': C.poi,
          'text-halo-color': C.halo,
          'text-halo-width': haloW(1), 'text-halo-blur': 0.25,
          'icon-opacity': 0.9
        }
      },
      {
        id: 'poi-symbol',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'poi',
        minzoom: 14,
        filter: ['all', ['==', ['geometry-type'], 'Point'], POI_SPLIT['poi-symbol']],
        layout: {
          'icon-image': ['coalesce', ['image', ['get', 'class']], ['image', 'dot_11']],
          'icon-size': 0.95,
          'text-field': ['step', ['zoom'], '', 15, NAME],
          'text-font': FONT,
          'text-size': ts([[7, 10], [13, 12], [18, 17]]),
          'text-anchor': 'top',
          'text-offset': [0, 0.8],
          'text-max-width': 8,
          'text-optional': true,
          'icon-allow-overlap': false
        },
        paint: {
          'text-color': C.poi,
          'text-halo-color': C.halo,
          'text-halo-width': haloW(1), 'text-halo-blur': 0.25,
          'icon-opacity': 0.95
        }
      },
      {
        id: 'spot-elevation',
        type: 'symbol',
        source: 'swisstopo',
        'source-layer': 'spot_elevation',
        minzoom: 13,
        layout: {
          'text-field': ['to-string', ['get', 'ele']],
          'text-font': FONT_ITALIC,
          'text-size': ts([[13, 11], [14, 11.5], [16, 14], [18, 15]]),
          'text-anchor': 'left',
          'text-offset': [0.5, 0],
          'icon-image': 'dot_9',
          'icon-size': 0.7,
          'text-optional': true
        },
        paint: {
          'text-color': C.peak,
          'text-halo-color': C.halo,
          'text-halo-width': haloW(1), 'text-halo-blur': 0.25,
          'icon-color': C.peak,
          'icon-halo-color': C.halo,
          'icon-halo-width': haloW(1)
        }
      },
      {
        id: 'label-area-name',
        type: 'symbol',
        source: 'swisstopo',
        'source-layer': 'area_name',
        minzoom: 13,
        layout: {
          'text-field': ['coalesce', ['get', 'name:de'], ['get', 'name']],
          'text-font': FONT_ITALIC,
          'text-size': ts([[13, 13], [18, 19]]),
          'text-max-width': 7,
          'text-letter-spacing': 0.04
        },
        paint: { 'text-color': C.inkSoft, 'text-halo-color': C.halo, 'text-halo-width': haloW(1), 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-place-small',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'place',
        minzoom: 11,
        // 'locality' und 'farm' greifen heute noch nicht: das OpenMapTiles-Schema
        // kennt diese Klassen nicht, deshalb landen Flurnamen gar nicht erst in
        // den Kacheln. Mit einem eigenen Planetiler-Schema wuerden sie erscheinen.
        filter: ['match', ['get', 'class'],
          ['village', 'hamlet', 'suburb', 'quarter', 'neighbourhood', 'isolated_dwelling',
           'locality', 'farm', 'island'], true, false],
        layout: {
          'text-field': NAME,
          'text-font': FONT,
          'text-size': ts([[11, 11], [14, 14], [18, 19]]),
          'text-max-width': 8
        },
        paint: { 'text-color': C.inkSoft, 'text-halo-color': C.halo, 'text-halo-width': haloW(1.25), 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-place-town',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'place',
        minzoom: 8,
        maxzoom: 17,
        filter: ['match', ['get', 'class'], ['town'], true, false],
        layout: {
          'text-field': NAME,
          'text-font': FONT_MEDIUM,
          'text-size': ts([[8, 12], [13, 17], [17, 24]]),
          'text-max-width': 8,
          // In der Uebersicht gemischt (Kriens, Horw), im groesseren Massstab
          // tritt der Hauptort in Versalien hervor.
          'text-transform': ['step', ['zoom'], 'none', 12, 'uppercase'],
          'text-letter-spacing': ['step', ['zoom'], 0, 12, 0.09]
        },
        paint: { 'text-color': C.ink, 'text-halo-color': C.halo, 'text-halo-width': haloW(1.25), 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-place-city',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'place',
        minzoom: 4,
        maxzoom: 14,
        filter: ['match', ['get', 'class'], ['city'], true, false],
        layout: {
          'text-field': NAME,
          'text-font': FONT_BOLD,
          'text-size': ts([[4, 13], [10, 22], [13, 30]]),
          'text-max-width': 9,
          'text-transform': 'uppercase',
          'text-letter-spacing': 0.12
        },
        paint: { 'text-color': C.ink, 'text-halo-color': C.halo, 'text-halo-width': haloW(1.25), 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-country',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'place',
        maxzoom: 9,
        filter: ['==', ['get', 'class'], 'country'],
        layout: {
          'text-field': NAME,
          'text-font': FONT_BOLD,
          'text-size': ts([[3, 11], [7, 16]]),
          'text-transform': 'uppercase',
          'text-letter-spacing': 0.18,
          'text-max-width': 7
        },
        paint: { 'text-color': C.inkSoft, 'text-halo-color': C.halo, 'text-halo-width': haloW(1.25), 'text-halo-blur': 0.25 }
      }
    ]
  };
}
